import { z } from 'zod';
import { getAddress, type Address } from 'viem';

// The branded canonical form: a ChecksummedAddress originates ONLY from this
// schema's parse (test fixtures aside) — the type itself carries the
// "EIP-55 canonical past the fetch boundary" guarantee, so contracts typing
// a field with it need no per-field canonicality prose. Never bless
// wagmi/viem-returned addresses with it — wallets return wallet-cased
// addresses, not checksummed ones. Known residual (TS structural limit): a
// branded === raw comparison still compiles, so for comparisons where a
// side does NOT come through a schema (wallet state, user input,
// localStorage) keep @/modules/is-same-address.
export type ChecksummedAddress = Address & {
  readonly __brand: 'ChecksummedAddress';
};

// Zod schema for Ethereum-address fields: validates AND canonicalizes at the
// parse boundary via viem's getAddress(), so downstream code only ever sees
// EIP-55 checksum form and can compare with plain ===. Accepts any input
// casing; note getAddress does NOT reject a wrong-checksum mixed-case
// input — it re-checksums it (checksum validation is not the point,
// canonical output is). Anything that isn't a 20-byte hex address fails the
// parse. The transform below is the ONE blessing site for the
// ChecksummedAddress brand: getAddress() output is canonical by
// construction.
export const AddressSchema: z.ZodType<ChecksummedAddress, string> = z
  .string()
  .transform((s, ctx): ChecksummedAddress => {
    try {
      return getAddress(s) as ChecksummedAddress;
    } catch {
      ctx.addIssue({
        code: 'custom',
        message: 'Invalid Ethereum address',
      });
      return z.NEVER;
    }
  });
